import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Eye, 
  MessageCircle, 
  Phone, 
  MousePointer, 
  Send, 
  Search, 
  Target,
  TrendingUp,
  Calendar,
  Lock,
  LogOut,
  RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);

  // Check if already authenticated (basic session)
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('dashAuth');
    if (savedAuth) {
      const { username, password } = JSON.parse(savedAuth);
      setUsername(username);
      setPassword(password);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch stats when authenticated
  useEffect(() => {
    if (isAuthenticated && username && password) {
      fetchStats();
    }
  }, [isAuthenticated, selectedDays]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (response.ok) {
        sessionStorage.setItem('dashAuth', JSON.stringify({ username, password }));
        setIsAuthenticated(true);
      } else {
        setAuthError('Credenciales incorrectas');
      }
    } catch (error) {
      setAuthError('Error de conexión');
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const credentials = btoa(`${username}:${password}`);
      const response = await fetch(`${API_URL}/api/analytics/stats?days=${selectedDays}`, {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('dashAuth');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setStats(null);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="bg-white/5 border border-white/10 rounded-sm p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <Lock className="h-12 w-12 text-neon mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white">Dashboard ArrancaYa</h1>
            <p className="text-white/60 text-sm mt-2">Acceso restringido</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-white/80">Usuario</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                placeholder="admin"
                data-testid="dash-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                placeholder="••••••••"
                data-testid="dash-password-input"
              />
            </div>

            {authError && (
              <p className="text-stark text-sm text-center">{authError}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-neon text-obsidian hover:bg-neon/90 font-bold rounded-sm"
              disabled={loading}
              data-testid="dash-login-btn"
            >
              {loading ? 'Verificando...' : 'Acceder'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard Content
  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="bg-obsidian/90 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <BarChart3 className="h-8 w-8 text-neon" />
            <h1 className="font-heading text-xl font-bold text-white">Dashboard ArrancaYa</h1>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
              className="bg-white/10 border border-white/20 text-white rounded-sm px-3 py-2 text-sm"
              data-testid="days-select"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={365}>Último año</option>
            </select>
            <Button
              onClick={fetchStats}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-sm"
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-stark/50 text-stark hover:bg-stark/10 rounded-sm"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && !stats ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-neon animate-spin" />
          </div>
        ) : stats ? (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Eye}
                label="Visitas Totales"
                value={stats.total_visits}
                color="neon"
              />
              <StatCard
                icon={MessageCircle}
                label="Clicks WhatsApp"
                value={stats.whatsapp_clicks}
                color="neon"
              />
              <StatCard
                icon={Phone}
                label="Clicks Teléfono"
                value={stats.phone_clicks}
                color="stark"
              />
              <StatCard
                icon={TrendingUp}
                label="Tasa Conversión"
                value={`${stats.conversion_rate}%`}
                color="neon"
              />
            </div>

            {/* Source Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Traffic Sources */}
              <div className="bg-white/5 border border-white/10 rounded-sm p-6">
                <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center">
                  <Target className="h-5 w-5 text-neon mr-2" />
                  Fuentes de Tráfico
                </h2>
                <div className="space-y-4">
                  <SourceBar 
                    label="SEO (Orgánico)" 
                    value={stats.seo_visits} 
                    total={stats.total_visits}
                    color="bg-neon"
                  />
                  <SourceBar 
                    label="SEA (Ads)" 
                    value={stats.sea_visits} 
                    total={stats.total_visits}
                    color="bg-stark"
                  />
                  <SourceBar 
                    label="Directo" 
                    value={stats.direct_visits} 
                    total={stats.total_visits}
                    color="bg-blue-500"
                  />
                  <SourceBar 
                    label="Otros" 
                    value={stats.total_visits - stats.seo_visits - stats.sea_visits - stats.direct_visits} 
                    total={stats.total_visits}
                    color="bg-white/30"
                  />
                </div>
              </div>

              {/* Engagement */}
              <div className="bg-white/5 border border-white/10 rounded-sm p-6">
                <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center">
                  <MousePointer className="h-5 w-5 text-neon mr-2" />
                  Engagement
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                    <p className="text-3xl font-bold text-neon">{stats.form_opens}</p>
                    <p className="text-white/60 text-sm">Formularios abiertos</p>
                  </div>
                  <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                    <p className="text-3xl font-bold text-neon">{stats.form_submits}</p>
                    <p className="text-white/60 text-sm">Formularios enviados</p>
                  </div>
                  <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                    <p className="text-3xl font-bold text-white">{stats.unique_sessions}</p>
                    <p className="text-white/60 text-sm">Sesiones únicas</p>
                  </div>
                  <div className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                    <p className="text-3xl font-bold text-stark">{stats.whatsapp_clicks + stats.phone_clicks}</p>
                    <p className="text-white/60 text-sm">Total contactos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pages Table */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center">
                <Search className="h-5 w-5 text-neon mr-2" />
                Páginas Más Visitadas
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="pages-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Página</th>
                      <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Visitas</th>
                      <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.pages.map((page, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4 text-white text-sm">{page.page || '/'}</td>
                        <td className="py-3 px-4 text-neon font-bold text-right">{page.views}</td>
                        <td className="py-3 px-4 text-white/60 text-right text-sm">
                          {stats.total_visits > 0 ? Math.round((page.views / stats.total_visits) * 100) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Daily Chart */}
            {stats.daily_stats.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-6">
                <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center">
                  <Calendar className="h-5 w-5 text-neon mr-2" />
                  Visitas por Día
                </h2>
                <div className="flex items-end space-x-1 h-40">
                  {stats.daily_stats.slice(-30).map((day, idx) => {
                    const maxVisits = Math.max(...stats.daily_stats.map(d => d.visits));
                    const height = maxVisits > 0 ? (day.visits / maxVisits) * 100 : 0;
                    return (
                      <div
                        key={idx}
                        className="flex-1 bg-neon/80 hover:bg-neon transition-colors rounded-t-sm cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${day.date}: ${day.visits} visitas`}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-obsidian border border-white/20 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {day.visits}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-white/40">
                  <span>{stats.daily_stats[0]?.date}</span>
                  <span>{stats.daily_stats[stats.daily_stats.length - 1]?.date}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-white/60">
            <p>No hay datos disponibles</p>
          </div>
        )}
      </main>
    </div>
  );
};


// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-sm p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`h-5 w-5 text-${color}`} />
    </div>
    <p className={`text-2xl md:text-3xl font-bold text-${color}`}>{value}</p>
    <p className="text-white/60 text-sm">{label}</p>
  </div>
);


// Source Bar Component
const SourceBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-white">{label}</span>
        <span className="text-white/60">{value} ({percentage}%)</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};


export default Dashboard;
